import React from 'react';
import {useTranslation} from 'react-i18next';
import {Avatar, Box, Stack, Tooltip, Typography, alpha, useTheme} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPhoneVolume, faTriangleExclamation, faVideo} from '@fortawesome/pro-solid-svg-icons';

import {CalendarEventDetail} from './calendarSources';
import {PRESENCE_COLOR, STATUS_COLOR, statusText} from './plannerStatus';
import {CATEGORY_COLOR, PlannerAgendaItem, PlannerAgendaPerson} from './plannerModel';

interface Props {
    people: PlannerAgendaPerson[];
    /** Kennungen der angemeldeten Person — ihre Karte wird hervorgehoben. */
    selfIdentifiers: string[];
    onSelect: (detail: CalendarEventDetail, ownMailbox: boolean) => void;
}

/** Kürzel aus dem Anzeigenamen. */
const initials = (label: string): string => label
    .split(/[\s,]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

/**
 * Die Planungstafel für Telefone — eine Karte je Person statt einer Zeitachse.
 *
 * **Warum keine Achse:** Auf einem iPhone SE bleiben für die Zeitachse rund 160 Punkte. Auf eine
 * Woche verteilt sind das gut dreißig Punkte je Tag; ein einstündiger Termin wird darin drei Punkte
 * breit. Das ist keine Frage der Gestaltung mehr — in dieser Breite lässt sich schlicht nichts
 * ablesen, und jede Feinarbeit an Balken und Raster ändert daran nichts.
 *
 * Die Frage, für die man die Tafel aufmacht, braucht die Achse aber gar nicht. Sie lautet „wer ist
 * gerade frei, und was steht an" — und die beantwortet eine Liste besser als ein Streifenmuster:
 * je Person ihr Zustand in Klartext, darunter ihr Tag. Nebeneinanderliegende Termine sieht man
 * dabei nicht mehr auf einen Blick; dafür ist am Schreibtisch die Achse da.
 */
export const PlannerMobile: React.FC<Props> = ({people, selfIdentifiers, onSelect}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    return (
        <Box>
            {people.map((person) => {
                const isSelf = selfIdentifiers.some(
                    (id) => id.toLowerCase() === person.identifier.toLowerCase());

                return (
                    <Box key={person.identifier} sx={{
                        borderTop: 1,
                        borderColor: 'divider',
                        // Die eigene Karte trägt dieselbe Marke wie die eigene Zeile auf der Tafel.
                        borderLeft: isSelf ? 3 : 0,
                        borderLeftColor: 'primary.main',
                        bgcolor: isSelf ? alpha(theme.palette.primary.main, 0.04) : undefined,
                    }}>
                        <Stack direction="row" spacing={1.25} sx={{px: 1.5, py: 1, alignItems: 'center'}}>
                            <Box sx={{position: 'relative', flexShrink: 0, display: 'flex'}}>
                                <Avatar sx={{
                                    width: 30, height: 30, fontSize: '0.72rem', fontWeight: 600,
                                    bgcolor: isSelf
                                        ? 'primary.main'
                                        : alpha(theme.palette.primary.main, 0.12),
                                    color: isSelf ? 'primary.contrastText' : 'primary.main',
                                }}>
                                    {initials(person.label)}
                                </Avatar>
                                {person.presence && (
                                    <Box sx={{
                                        position: 'absolute',
                                        right: -1,
                                        bottom: -1,
                                        width: 11,
                                        height: 11,
                                        borderRadius: '50%',
                                        bgcolor: PRESENCE_COLOR[person.presence.state],
                                        border: 2,
                                        borderColor: 'background.paper',
                                        boxSizing: 'content-box',
                                    }}/>
                                )}
                            </Box>

                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                <Typography variant="body2" sx={{
                                    fontWeight: isSelf ? 700 : 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {person.label}
                                </Typography>

                                {/*
                                  * Der Zustand in Klartext ist auf dem Telefon die Hauptaussage —
                                  * am Schreibtisch liest man ihn aus der Lage der Balken ab, hier
                                  * muss er dastehen.
                                  */}
                                <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                                    {person.presence?.inCall && (
                                        <Box component="span" sx={{
                                            color: PRESENCE_COLOR[person.presence.state],
                                            display: 'inline-flex',
                                            fontSize: '0.65rem',
                                            flexShrink: 0,
                                        }}>
                                            <FontAwesomeIcon icon={faPhoneVolume}/>
                                        </Box>
                                    )}
                                    {!person.presence && person.status !== 'unknown' && (
                                        <Box sx={{
                                            width: 7, height: 7, borderRadius: '50%',
                                            bgcolor: STATUS_COLOR[person.status],
                                            flexShrink: 0,
                                        }}/>
                                    )}
                                    <Typography variant="caption" sx={{
                                        color: person.presence
                                            ? PRESENCE_COLOR[person.presence.state]
                                            : 'text.secondary',
                                        fontWeight: person.presence ? 600 : 400,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {person.presence
                                            ? t(person.presence.key)
                                            : statusText(person.status, person.statusUntil, t)}
                                    </Typography>
                                </Stack>
                            </Box>

                            {person.notes.length > 0 && (
                                <Tooltip title={person.notes.join(' · ')}>
                                    <Box component="span" sx={{color: 'warning.main', display: 'inline-flex'}}>
                                        <FontAwesomeIcon icon={faTriangleExclamation}/>
                                    </Box>
                                </Tooltip>
                            )}
                        </Stack>

                        {person.items.length === 0 ? (
                            <Typography variant="caption" color="text.secondary"
                                        sx={{display: 'block', px: 1.5, pb: 1, pl: 6}}>
                                {t('planner.noEntries')}
                            </Typography>
                        ) : (
                            <Box sx={{pb: 0.5}}>
                                {person.items.map((item) => (
                                    <AgendaRow key={item.key} item={item}
                                               onSelect={(detail) => onSelect(detail, isSelf)}/>
                                ))}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

/** Ein Eintrag unter einer Person: Uhrzeit, Betreff, Herkunft als farbige Kante. */
const AgendaRow: React.FC<{
    item: PlannerAgendaItem;
    onSelect: (detail: CalendarEventDetail) => void;
}> = ({item, onSelect}) => {
    const {t} = useTranslation();
    const color = CATEGORY_COLOR[item.category];

    return (
        <Stack direction="row" spacing={1} onClick={() => onSelect(item.detail)} sx={{
            // Eingerückt bis unter den Namen: die Einträge gehören sichtbar zur Person darüber.
            ml: 1.5,
            mr: 1.5,
            mt: 0.5,
            px: 1,
            py: 0.75,
            alignItems: 'flex-start',
            borderLeft: '3px solid',
            borderLeftColor: color,
            borderRadius: '3px',
            bgcolor: alpha(color, item.free ? 0.06 : 0.12),
            cursor: 'pointer',
            // Unter 40 Punkten trifft die Fingerkuppe daneben.
            minHeight: 40,
        }}>
            <Typography variant="caption" sx={{
                minWidth: 40,
                flexShrink: 0,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: 'text.secondary',
                lineHeight: 1.4,
            }}>
                {item.allDay ? t('calendar.allDay') : item.from}
            </Typography>

            <Box sx={{minWidth: 0, flexGrow: 1}}>
                <Stack direction="row" spacing={0.75} sx={{alignItems: 'center'}}>
                    {item.online && (
                        <Box component="span" sx={{
                            color, display: 'inline-flex', fontSize: '0.65rem', flexShrink: 0,
                        }}>
                            <FontAwesomeIcon icon={faVideo}/>
                        </Box>
                    )}
                    <Typography variant="body2" sx={{
                        fontSize: '0.8rem',
                        lineHeight: 1.35,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}>
                        {item.title}
                    </Typography>
                </Stack>
            </Box>

            {!item.allDay && item.to && (
                <Typography variant="caption" color="text.secondary" sx={{
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.4,
                }}>
                    {item.to}
                </Typography>
            )}
        </Stack>
    );
};

export default PlannerMobile;
